// [d8f02a7f] Sino de notificações in-app no header.
// Mostra contador de não-lidas e dropdown com últimas 30 notificações.
// Polling: a cada 60s busca nova contagem (silenciosamente).
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Notif {
  id: string;
  tipo: string;
  titulo: string;
  mensagem?: string;
  icone?: string;
  link_modulo?: string;
  link_pagina?: string;
  link_id?: string;
  meta?: any;
  lida: boolean;
  criado_em: string;
}

const ICONES_PADRAO: Record<string, string> = {
  atividade_atribuida: '📋',
  atividade_prazo:     '⏰',
  mensagem_dwight:     '◇',
  contrato_status:     '⚙️',
  mencao:              '💬',
  mencao_mkt:          '💬',
  mencao_cr:           '💬',
  mkt_post:            '📱',
  alerta:              '⚠️',
  info:                'ℹ️',
};

function tempoRel(ts: string): string {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d atrás`;
  return new Date(ts).toLocaleDateString('pt-BR');
}

export default function NotificacoesBell({ onIrPara }: {
  onIrPara: (modulo: string, pagina: string, id?: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState<Notif[]>([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Carrega na primeira render + a cada 60s
  useEffect(() => {
    carregar();
    const i = setInterval(carregar, 60_000);
    return () => clearInterval(i);
  }, []);

  // Fecha ao clicar fora
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    }
    if (aberto) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [aberto]);

  async function carregar() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'notif_listar', limit: 30 },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setItens(Array.isArray(data?.itens) ? data.itens : []);
      setNaoLidas(data?.nao_lidas || 0);
    } catch {}
  }

  async function abrir() {
    setAberto(!aberto);
    if (!aberto && itens.length === 0) {
      setCarregando(true);
      await carregar();
      setCarregando(false);
    }
  }

  async function marcarLida(id: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'notif_marcar_lida', id },
        { headers: { Authorization: token } });
      setItens(its => its.map(n => n.id === id ? { ...n, lida: true } : n));
      setNaoLidas(c => Math.max(0, c - 1));
    } catch {}
  }

  async function marcarTodas() {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'notif_marcar_todas' },
        { headers: { Authorization: token } });
      setItens(its => its.map(n => ({ ...n, lida: true })));
      setNaoLidas(0);
    } catch {}
  }

  async function clicarItem(n: Notif) {
    if (!n.lida) await marcarLida(n.id);
    if (n.link_modulo && n.link_pagina) {
      onIrPara(n.link_modulo, n.link_pagina, n.link_id);
      setAberto(false);
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Sino */}
      <button onClick={abrir} aria-label="Notificações"
        style={{
          background: aberto ? '#E7ECFD' : 'transparent',
          border: 'none', cursor: 'pointer',
          width: 36, height: 36, borderRadius: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
          color: '#0930D0',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!aberto) (e.currentTarget as HTMLElement).style.background = '#F4F5F8'; }}
        onMouseLeave={e => { if (!aberto) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {naoLidas > 0 && (
          <span style={{
            position: 'absolute', top: 4, right: 4,
            background: '#B91626', color: '#fff',
            fontSize: 9, fontWeight: 700,
            minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid #fff',
          }}>{naoLidas > 99 ? '99+' : naoLidas}</span>
        )}
      </button>

      {/* Dropdown */}
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          width: 380, maxWidth: 'calc(100vw - 32px)',
          maxHeight: '70vh', overflowY: 'auto',
          background: '#fff',
          border: '0.5px solid #e0e0e0', borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
          zIndex: 1000,
        }}>
          <div style={{ padding: '12px 14px', borderBottom: '0.5px solid #f0f0f0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <strong style={{ fontSize: 13, color: '#020D7F' }}>
              Notificações {naoLidas > 0 && <span style={{ color: '#B91626', fontWeight: 600 }}>· {naoLidas} novas</span>}
            </strong>
            {naoLidas > 0 && (
              <button onClick={marcarTodas}
                style={{ fontSize: 11, padding: '3px 8px',
                  background: 'transparent', border: '0.5px solid #ddd',
                  borderRadius: 4, cursor: 'pointer', color: '#0930D0', fontWeight: 500 }}>
                Marcar todas como lidas
              </button>
            )}
          </div>

          {carregando ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#888', fontSize: 12 }}>Carregando...</div>
          ) : itens.length === 0 ? (
            <div style={{ padding: 36, textAlign: 'center', color: '#aaa',
              fontStyle: 'italic', fontSize: 12 }}>
              Sem notificações por enquanto.
            </div>
          ) : (
            <div>
              {itens.map(n => {
                const icone = n.icone || ICONES_PADRAO[n.tipo] || 'ℹ️';
                const clicavel = !!n.link_modulo;
                return (
                  <div key={n.id} onClick={() => clicarItem(n)}
                    style={{
                      padding: '10px 14px',
                      background: n.lida ? '#fff' : '#FAFBFF',
                      borderLeft: n.lida ? '3px solid transparent' : '3px solid #0930D0',
                      borderBottom: '0.5px solid #f5f5f5',
                      cursor: clicavel ? 'pointer' : 'default',
                      display: 'flex', gap: 10,
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => clicavel && ((e.currentTarget as HTMLElement).style.background = '#F4F5F8')}
                    onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = n.lida ? '#fff' : '#FAFBFF')}>
                    <div style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{icone}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: n.lida ? 400 : 600,
                        color: '#222', lineHeight: 1.3 }}>
                        {n.titulo}
                      </div>
                      {n.mensagem && (
                        <div style={{ fontSize: 11, color: '#666', marginTop: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {n.mensagem}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                        {tempoRel(n.criado_em)}
                      </div>
                    </div>
                    {!n.lida && (
                      <span style={{ width: 8, height: 8, borderRadius: 4,
                        background: '#0930D0', alignSelf: 'center', flexShrink: 0 }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
